// src/component/ui/Dialog.jsx

import React, { useEffect } from 'react';
import MuiDialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(MuiDialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    backdropFilter: 'blur(8px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    padding: '16px',
    boxShadow: theme.shadows[8],
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: '16px',
  top: '16px',
  color: theme.palette.text.secondary,
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: '12px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

/**
 * CustomDialog - Accessible premium dialog container with blurred backdrop
 * @param {Object} props
 * @param {boolean} props.open - State parameter toggling dialog display
 * @param {function} props.onClose - Triggered on ESC key, backdrop click, or Close icon click
 * @param {string} props.title - Title text
 * @param {React.ReactNode} props.children - Dialog body content
 * @param {React.ReactNode} [props.actions] - Bottom actions content
 * @param {string} [props.maxWidth="sm"] - Size configuration (xs, sm, md, lg, xl)
 */
export const Dialog = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'sm',
  ...rest
}) => {
  // Listen for Escape key to close dialog
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby="dialog-title"
      {...rest}
    >
      <DialogTitle id="dialog-title" style={{ paddingRight: '48px', paddingBottom: '8px' }}>
        <Typography variant="h2" component="h2" style={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <CloseButton onClick={onClose} aria-label="Close dialog" size="small">
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>
      
      <DialogContent style={{ padding: '8px 24px 24px 24px' }}>
        {children}
      </DialogContent>
      
      {actions && (
        <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid var(--color-border)', gap: '12px' }}>
          {actions}
        </DialogActions>
      )}
    </StyledDialog>
  );
};

export default Dialog;
